import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Grid, List, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductFilters from '../components/ProductFilters';
import { unifiedAPI } from '../utils/api';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import RecentlyViewed from '../components/RecentlyViewed';
import { useStableSearchParams } from '../hooks/useStableSearchParams';

const ProductListPage = () => {
  const { categoryId } = useParams();
  const { initialParams, setParams } = useStableSearchParams();
  const { recentlyViewed } = useRecentlyViewed();
  
  // State
  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  
  // Read initial state from URL params (only on mount)
  const [filters, setFilters] = useState(() => ({
    categories: categoryId ? [categoryId] : (initialParams.categories?.split(',').filter(Boolean) || []),
    sort: initialParams.sort || 'name_asc',
    minPrice: initialParams.min_price ? parseFloat(initialParams.min_price) : null,
    maxPrice: initialParams.max_price ? parseFloat(initialParams.max_price) : null,
    status: initialParams.status || null,
    page: parseInt(initialParams.page || '1'),
    search: initialParams.search || '',
  }));

  const ITEMS_PER_PAGE = 24;
  
  // Track if first render to skip initial URL update
  const isFirstRender = useRef(true);

  // Fetch categories for sidebar
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await unifiedAPI.getCategories({ product_type: 'small_format', include_counts: true });
        setAllCategories(response.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Update filters when categoryId param changes
  useEffect(() => {
    if (categoryId) {
      setFilters(prev => ({ ...prev, categories: [categoryId], page: 1 }));
    }
  }, [categoryId]);

  // Update URL when filters change (using stable hook)
  useEffect(() => {
    // Skip first render - we just read from URL
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    const params = {};
    if (!categoryId && filters.categories?.length) {
      params.categories = filters.categories.join(',');
    }
    if (filters.sort && filters.sort !== 'name_asc') params.sort = filters.sort;
    if (filters.minPrice !== null) params.min_price = filters.minPrice;
    if (filters.maxPrice !== null) params.max_price = filters.maxPrice;
    if (filters.status) params.status = filters.status;
    if (filters.page > 1) params.page = filters.page;
    if (filters.search) params.search = filters.search;
    
    setParams(params);
  }, [filters, categoryId, setParams]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await unifiedAPI.getProducts({
        product_type: 'small_format',
        category_id: filters.categories.length === 1 ? filters.categories[0] : undefined,
        page: filters.page,
        limit: ITEMS_PER_PAGE,
        sort: filters.sort,
        search: filters.search || undefined,
      });
      
      setProducts(response.products || []);
      setTotalProducts(response.total || 0);
      // Note: filter_options not available in unifiedAPI, would need backend enhancement
      setFilterOptions(null);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch products when filters change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Filter handlers
  const handleSortChange = (sort) => {
    setFilters(prev => ({ ...prev, sort, page: 1 }));
  };

  const handleCategoryChange = (categories) => {
    setFilters(prev => ({ ...prev, categories, page: 1 }));
  };

  const handlePriceChange = (minPrice, maxPrice) => {
    setFilters(prev => ({ ...prev, minPrice, maxPrice, page: 1 }));
  };

  const handleStatusChange = (status) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      categories: categoryId ? [categoryId] : [],
      sort: 'name_asc',
      minPrice: null,
      maxPrice: null,
      status: null,
      page: 1,
      search: '',
    });
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryClick = (catId) => {
    if (filters.categories.includes(catId)) {
      setFilters(prev => ({ ...prev, categories: prev.categories.filter(c => c !== catId), page: 1 }));
    } else {
      setFilters(prev => ({ ...prev, categories: [catId], page: 1 }));
    }
  };

  // Pagination
  const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE);

  // Get current category name for header
  const currentCategory = allCategories.find(c => filters.categories.includes(c.id));

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Header */}
      <section className="relative overflow-hidden py-12 gradient-hero">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold-500/5 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-4 transition-colors font-body"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800" data-testid="products-heading">
            {currentCategory ? currentCategory.name : 'All Products'}
          </h1>
          <p className="text-ivory-500 mt-2 font-body">
            {currentCategory ? currentCategory.description : 'Browse our complete catalog of small format printing products'}
          </p>
        </div>
      </section>

      {/* Content with Sidebar */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Categories */}
          <aside className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 shadow-luxury p-4 sticky top-4">
              <h2 className="text-lg font-heading font-semibold text-ivory-800 mb-4 pb-3 border-b border-ivory-200">
                Categories
              </h2>
              
              {categoriesLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 text-gold-500 animate-spin" />
                </div>
              ) : (
                <nav className="space-y-1" data-testid="category-sidebar">
                  {/* All Products link */}
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, categories: [], page: 1 }))}
                    className={`w-full text-left px-3 py-2 rounded-lg font-body text-sm transition-colors flex items-center justify-between ${
                      filters.categories.length === 0
                        ? 'bg-gold-500/10 text-gold-700 font-medium'
                        : 'text-ivory-600 hover:bg-ivory-200/50 hover:text-ivory-800'
                    }`}
                    data-testid="category-all"
                  >
                    <span>All Products</span>
                    <span className="text-xs text-ivory-400">
                      {allCategories.reduce((sum, c) => sum + (c.count || 0), 0)}
                    </span>
                  </button>
                  
                  {/* Category list */}
                  {allCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryClick(category.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg font-body text-sm transition-colors flex items-center justify-between group ${
                        filters.categories.includes(category.id)
                          ? 'bg-gold-500/10 text-gold-700 font-medium'
                          : 'text-ivory-600 hover:bg-ivory-200/50 hover:text-ivory-800'
                      }`}
                      data-testid={`category-sidebar-${category.id}`}
                    >
                      <span className="flex items-center gap-2">
                        <ChevronRight className={`w-4 h-4 transition-transform ${
                          filters.categories.includes(category.id) ? 'text-gold-600' : 'text-ivory-400 group-hover:translate-x-0.5'
                        }`} />
                        {category.name}
                      </span>
                      <span className="text-xs text-ivory-400">{category.count || 0}</span>
                    </button>
                  ))}
                </nav>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Filters Bar (Sort, Price Range) */}
            <ProductFilters
              filterOptions={filterOptions}
              selectedCategories={[]}
              selectedSort={filters.sort}
              minPrice={filters.minPrice}
              maxPrice={filters.maxPrice}
              selectedStatus={filters.status}
              onCategoryChange={handleCategoryChange}
              onSortChange={handleSortChange}
              onPriceChange={handlePriceChange}
              onStatusChange={handleStatusChange}
              onClearFilters={handleClearFilters}
              loading={loading}
              hideCategoryFilter={true}
            />

            {/* Results Summary & View Toggle */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-ivory-600 font-body">
                {loading ? 'Loading...' : `${totalProducts} product${totalProducts !== 1 ? 's' : ''} found`}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-gold-500 text-white' : 'bg-ivory-200 text-ivory-600 hover:bg-ivory-300'}`}
                  data-testid="view-grid"
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-gold-500 text-white' : 'bg-ivory-200 text-ivory-600 hover:bg-ivory-300'}`}
                  data-testid="view-list"
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
              </div>
            )}

            {/* No Results */}
            {!loading && products.length === 0 && (
              <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 p-12 text-center">
                <p className="text-ivory-600 font-body mb-4">No products found matching your filters.</p>
                <Button onClick={handleClearFilters} className="btn-luxury-outline">
                  Clear Filters
                </Button>
              </div>
            )}

            {/* Products Grid */}
            {!loading && products.length > 0 && (
              <>
                <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6' 
                  : 'space-y-4'
                }>
                  {products.map((product) => (
                    <Link
                      key={product.id}
                      to={`/product/${product.id}`}
                      className="group"
                      data-testid={`product-card-${product.id}`}
                    >
                      {viewMode === 'grid' ? (
                        <div className="h-full rounded-xl bg-ivory-100 border border-ivory-300/50 overflow-hidden card-luxury">
                          <div className="aspect-square overflow-hidden bg-ivory-200">
                            <img
                              src={product.image || 'https://placehold.co/400x400/f5f3f0/9a8b7d?text=Product'}
                              alt={product.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              onError={(e) => { e.target.src = 'https://placehold.co/400x400/f5f3f0/9a8b7d?text=Product'; }}
                            />
                          </div>
                          <div className="p-5">
                            {product.featured && (
                              <span className="inline-block px-2.5 py-1 text-xs font-medium bg-gold-500/10 text-gold-700 rounded-full mb-3 font-body">
                                Featured
                              </span>
                            )}
                            <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-2 group-hover:text-gold-700 transition-colors line-clamp-2">
                              {product.name}
                            </h3>
                            <p className="text-ivory-500 text-sm mb-4 font-body line-clamp-2">
                              {product.description}
                            </p>
                            <div className="flex items-center justify-between">
                              <p className="text-lg font-heading font-semibold text-gold-600">
                                From ${product.basePrice?.toFixed(2) || '0.00'}
                              </p>
                              <span className="text-sm text-ivory-400 group-hover:text-gold-600 transition-colors font-body">
                                View Details →
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-6 p-4 rounded-xl bg-ivory-100 border border-ivory-300/50 card-luxury">
                          <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-ivory-200">
                            <img
                              src={product.image || 'https://placehold.co/200x200/f5f3f0/9a8b7d?text=Product'}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.src = 'https://placehold.co/200x200/f5f3f0/9a8b7d?text=Product'; }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            {product.featured && (
                              <span className="inline-block px-2.5 py-1 text-xs font-medium bg-gold-500/10 text-gold-700 rounded-full mb-2 font-body">
                                Featured
                              </span>
                            )}
                            <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-1 group-hover:text-gold-700 transition-colors">
                              {product.name}
                            </h3>
                            <p className="text-ivory-500 text-sm mb-3 font-body line-clamp-2">
                              {product.description}
                            </p>
                            <p className="text-lg font-heading font-semibold text-gold-600">
                              From ${product.basePrice?.toFixed(2) || '0.00'}
                            </p>
                          </div>
                        </div>
                      )}
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-12">
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(filters.page - 1)}
                      disabled={filters.page === 1}
                      className="btn-luxury-outline"
                    >
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (filters.page <= 3) {
                          pageNum = i + 1;
                        } else if (filters.page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = filters.page - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-10 h-10 rounded-lg font-body text-sm transition-colors ${
                              filters.page === pageNum
                                ? 'bg-gold-500 text-white'
                                : 'bg-ivory-200 text-ivory-600 hover:bg-ivory-300'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(filters.page + 1)}
                      disabled={filters.page === totalPages}
                      className="btn-luxury-outline"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Recently Viewed Section */}
            {recentlyViewed.length > 0 && (
              <RecentlyViewed products={recentlyViewed} currentProductId={null} />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductListPage;
